// ─────────────────────────────────────────────
// src/pages/HomePage.jsx
// ─────────────────────────────────────────────
import { useState, useEffect, useRef } from "react";
import Spline from "@splinetool/react-spline";
import { StarField, GlassCard, Btn, SectionLabel, SectionTitle } from "../components/UI";
import Footer from "../components/Footer";
import { LEARNING_AREAS } from "../data/constants";
import { sendNewsletterSignup, sendContactMessage } from "../utils/email";

export default function HomePage({ setPage }) {
  const [splineReady, setSplineReady] = useState(false);
  const splineWrapRef = useRef(null);

  useEffect(() => {
    function forwardMouseMove(e) {
      const canvas = splineWrapRef.current?.querySelector("canvas");
      if (!canvas) return;
      canvas.dispatchEvent(new MouseEvent("mousemove", {
        bubbles: true,
        clientX: e.clientX,
        clientY: e.clientY,
        screenX: e.screenX,
        screenY: e.screenY,
        movementX: e.movementX,
        movementY: e.movementY,
      }));
    }
    window.addEventListener("mousemove", forwardMouseMove);
    return () => window.removeEventListener("mousemove", forwardMouseMove);
  }, []);
  const [email, setEmail] = useState("");
  const [subDone, setSubDone] = useState(false);
  const [subSending, setSubSending] = useState(false);
  const [contactEmail, setContactEmail] = useState("");
  const [contactMsg, setContactMsg] = useState("");
  const [contactSending, setContactSending] = useState(false);
  const [contactDone, setContactDone] = useState(false);

  async function handleSubscribe() {
    if (!email || subDone) return;
    setSubSending(true);
    try {
      await sendNewsletterSignup(email);
    } catch (err) {
      console.error("EmailJS error:", err);
    }
    setSubSending(false);
    setSubDone(true);
  }

  async function handleContact() {
    if (!contactEmail || !contactMsg || contactSending) return;
    setContactSending(true);
    try {
      await sendContactMessage(contactEmail, contactMsg);
    } catch (err) {
      console.error("EmailJS error:", err);
    }
    setContactSending(false);
    setContactDone(true);
  }

  const floaters = [
    { e:"🌕", top:"8%",    right:"6%", sz:44, an:"float2", dur:5   },
    { e:"☄️", top:"38%",   left:"2%",  sz:28, an:"float3", dur:3.5 },
    { e:"🌍", bottom:"20%",right:"4%", sz:46, an:"float1", dur:4.5 },
    { e:"⭐", bottom:"32%",left:"8%",  sz:26, an:"float2", dur:3   },
    { e:"🛸", top:"58%",   right:"9%", sz:34, an:"float3", dur:5   },
  ];

  return (
    <div>
      {/* ── HERO ── */}
      <section style={{ position:"relative", minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:"7rem 2rem 4rem", overflowX:"hidden" }}>
        <StarField />

        {/* Spline 3D background */}
        <div ref={splineWrapRef} className={`spline-bg${splineReady ? " spline-ready" : ""}`} style={{ position:"absolute", inset:0, zIndex:0, pointerEvents:"none", opacity:0 }}>
          <Spline scene="https://prod.spline.design/BHAa6dWu3fd4Za8g/scene.splinecode" onLoad={() => setSplineReady(true)} />
          <div style={{ position:"absolute", inset:0, background:"linear-gradient(to right, #080B26 0%, transparent 25%, transparent 75%, #080B26 100%)", zIndex:1, pointerEvents:"none" }} />
          <div style={{ position:"absolute", inset:0, background:"linear-gradient(to bottom, #080B26 0%, transparent 20%, transparent 70%, #080B26 100%)", zIndex:1, pointerEvents:"none" }} />
        </div>
        <div style={{ position:"absolute", bottom:0, right:0, width:300, height:60, background:"#080B26", zIndex:10, pointerEvents:"none" }} />

        {floaters.map((d, i) => (
          <div key={i} style={{ position:"absolute", top:d.top, bottom:d.bottom, left:d.left, right:d.right, fontSize:d.sz, animation:`${d.an} ${d.dur}s ease-in-out infinite`, zIndex:1, pointerEvents:"none" }}>{d.e}</div>
        ))}
        <div style={{ position:"absolute", width:600, height:600, borderRadius:"50%", background:"radial-gradient(circle,rgba(255,107,107,0.1) 0%,transparent 70%)", top:"50%", left:"50%", transform:"translate(-50%,-50%)", pointerEvents:"none" }} />

        <div style={{ maxWidth:860, textAlign:"center", position:"relative", zIndex:2, animation:"fadeUp 0.9s ease both" }}>
          <h1 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"clamp(2.4rem,7.5vw,5rem)", lineHeight:1.1, marginBottom:"1.4rem" }}>
            Launch Your Child's <span style={{ color:"#FFD93D" }}>Future</span><br/>
            with Mars Academy
          </h1>
          <p style={{ fontSize:"clamp(0.97rem,2.3vw,1.18rem)", color:"rgba(255,255,255,0.68)", maxWidth:640, margin:"0 auto 2.6rem", lineHeight:1.78, fontWeight:600 }}>
            One comprehensive program, offered before and after school, teaches elementary and middle school students coding, AI, internet safety, the cloud, digital infrastructure, and more, using hands-on, space-themed adventures they'll love.
          </p>
          <div style={{ display:"flex", gap:"1rem", justifyContent:"center", flexWrap:"wrap", marginBottom:"3.2rem" }}>
            <Btn onClick={() => setPage("login")} variant="primary" style={{ fontSize:"1rem", padding:"14px 36px" }}>🚀 Sign Up Now</Btn>
            <Btn onClick={() => setPage("programs")} variant="secondary" style={{ fontSize:"1rem", padding:"14px 34px" }}>View Program</Btn>
          </div>
          <div style={{ display:"flex", justifyContent:"center", gap:"2.5rem", flexWrap:"wrap" }}>
            {[["K+","Available to All Grades"],["6","Skills in 1 Program"],["2x","Growth In a Year"],["1-2 hrs","Per Class"]].map(([n, l]) => (
              <div key={l} style={{ textAlign:"center" }}>
                <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.9rem", color:"#FFD93D" }}>{n}</div>
                <div style={{ fontSize:"0.78rem", color:"rgba(255,255,255,0.45)", fontWeight:700 }}>{l}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign:"center", marginTop:"2.2rem" }}>
            <p style={{ color:"rgba(255,255,255,0.4)", fontSize:"0.72rem", fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:"1rem" }}>Certified By</p>
            <a href="https://www.idc-a.org/" target="_blank" rel="noopener noreferrer" style={{ display:"inline-flex", alignItems:"center", gap:"0.5rem", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:10, padding:"0.5rem 1.2rem", textDecoration:"none", color:"rgba(255,255,255,0.8)", fontFamily:"'Fredoka One',cursive", fontSize:"0.95rem" }}>
              🏅 International Data Center Authority | IDCA
            </a>
            <img src="/idca-certified.png" alt="IDCA Certified badge — Mars Academy curriculum certified by the International Data Center Authority" style={{ height:"140px", maxWidth:"90vw", width:"auto", objectFit:"contain", filter:"drop-shadow(0 2px 12px rgba(0,0,0,0.5))", display:"block", margin:"0.9rem auto 0" }} />
          </div>
        </div>
      </section>

      {/* ── MISSION ── */}
      <section style={{ padding:"4rem 2rem", background:"linear-gradient(135deg,rgba(155,92,246,0.1),rgba(255,107,107,0.08))", borderTop:"1px solid rgba(255,255,255,0.07)", borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
        <div style={{ maxWidth:800, margin:"0 auto", textAlign:"center" }}>
          <SectionLabel color="#9B5CF6">Our Mission</SectionLabel>
          <h2 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"clamp(1.6rem,4vw,2.4rem)", lineHeight:1.2, marginBottom:"1.2rem" }}>
            We Want Kids to Be <span style={{ color:"#FFD93D" }}>Ahead of Their Time</span> 🌟
          </h2>
          <p style={{ color:"rgba(255,255,255,0.72)", fontSize:"1.08rem", lineHeight:1.85, fontWeight:600, maxWidth:680, margin:"0 auto" }}>
            Our mission is to teach the young generation the technology that will define their future — before that future arrives. From coding and AI to digital infrastructure and cybersecurity, Mars Academy ensures every student graduates with real skills, real confidence, and a real head start on tomorrow.
          </p>
        </div>
      </section>

      {/* ── LEARNING AREAS ── */}
      <section style={{ padding:"5rem 2rem", maxWidth:1200, margin:"0 auto" }}>
        <div style={{ textAlign:"center", marginBottom:"2.8rem" }}>
          <SectionLabel color="#FF6B6B">Core Curriculum</SectionLabel>
          <SectionTitle center>Everything in One Program 🛸</SectionTitle>
          <p style={{ color:"rgba(255,255,255,0.5)", fontSize:"1rem", fontWeight:600, maxWidth:500, margin:"0 auto", lineHeight:1.7 }}>
            One cohesive program. Typically 6 classes per session, ~2 hours each (varies by school). Six essential tech areas covered.
          </p>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:"1.2rem" }}>
          {LEARNING_AREAS.map((a, i) => (
            <GlassCard key={i} style={{ boxSizing:"border-box" }}>
              <div style={{ width:56, height:56, borderRadius:15, background:a.color+"20", border:`2px solid ${a.color}38`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.6rem", marginBottom:"0.9rem" }}>{a.icon}</div>
              <h3 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.05rem", marginBottom:"0.55rem", color:a.color }}>{a.title}</h3>
              <p style={{ color:"rgba(255,255,255,0.5)", fontSize:"0.85rem", lineHeight:1.65, fontWeight:600 }}>{a.desc}</p>
            </GlassCard>
          ))}
        </div>
      </section>


      {/* ── LOCAL FOCUS ── */}
      <section style={{ padding:"1rem 2rem 3.5rem", maxWidth:900, margin:"0 auto" }}>
        <GlassCard style={{ textAlign:"center", padding:"2.4rem 2rem" }}>
          <h2 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.35rem", marginBottom:"0.6rem" }}>Proudly Based in Fairfax, Virginia 🏫</h2>
          <p style={{ color:"rgba(255,255,255,0.55)", fontSize:"0.95rem", fontWeight:600, lineHeight:1.7, marginBottom:"1.3rem", maxWidth:560, margin:"0 auto 1.3rem" }}>
            Mars Academy partners with elementary and middle schools across Fairfax County and Northern Virginia to bring our before- and after-school STEM and coding program directly into classrooms kids already know.
          </p>
          <div style={{ display:"flex", gap:"1.4rem", justifyContent:"center", flexWrap:"wrap" }}>
            <button
              onClick={() => setPage("after-school-programs")}
              style={{ background:"none", border:"none", color:"#FFD93D", fontFamily:"inherit", fontWeight:800, fontSize:"0.92rem", cursor:"pointer", display:"inline-flex", alignItems:"center", gap:"0.4rem" }}
            >
              Before & After School Programs →
            </button>
            <button
              onClick={() => setPage("fairfax-va")}
              style={{ background:"none", border:"none", color:"#06B6D4", fontFamily:"inherit", fontWeight:800, fontSize:"0.92rem", cursor:"pointer", display:"inline-flex", alignItems:"center", gap:"0.4rem" }}
            >
              Serving Fairfax, VA →
            </button>
          </div>
        </GlassCard>
      </section>

      {/* ── FAQ TEASER ── */}
      <section style={{ padding:"3.5rem 2rem", maxWidth:640, margin:"0 auto" }}>
        <GlassCard style={{ textAlign:"center", padding:"2.4rem 2rem" }}>
          <h3 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.35rem", marginBottom:"0.6rem" }}>Have questions? 🙋</h3>
          <p style={{ color:"rgba(255,255,255,0.55)", fontSize:"0.95rem", fontWeight:600, lineHeight:1.7, marginBottom:"1.3rem", maxWidth:440, margin:"0 auto 1.3rem" }}>
            Find answers to common questions about our programs, registration, and partnerships.
          </p>
          <button
            onClick={() => setPage("faq")}
            style={{ background:"none", border:"none", color:"#FFD93D", fontFamily:"inherit", fontWeight:800, fontSize:"0.95rem", cursor:"pointer", display:"inline-flex", alignItems:"center", gap:"0.4rem" }}
          >
            Visit our FAQ <span style={{ transition:"transform 0.2s" }}>→</span>
          </button>
        </GlassCard>
      </section>

      {/* ── CONTACT US ── */}
      <section style={{ padding:"5rem 2rem", maxWidth:780, margin:"0 auto", textAlign:"center" }}>
        <div style={{ background:"linear-gradient(135deg,rgba(255,107,107,0.12),rgba(155,92,246,0.12))", border:"1.5px solid rgba(255,255,255,0.1)", borderRadius:28, padding:"3.5rem 2rem" }}>
          <SectionTitle center>Contact Us</SectionTitle>
          <p style={{ color:"rgba(255,255,255,0.55)", marginBottom:"1.8rem", fontSize:"0.97rem", fontWeight:600, lineHeight:1.7 }}>
            Have questions? We'd love to hear from you. Send us a message and we'll get back to you as soon as possible.
          </p>
          {contactDone ? (
            <p style={{ color:"rgba(100,255,180,0.9)", fontSize:"1rem", fontWeight:700 }}>Message sent! We'll be in touch soon.</p>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:"0.8rem", maxWidth:480, margin:"0 auto" }}>
              <input
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="Your email address *"
                style={{ background:"rgba(255,255,255,0.08)", border:"1.5px solid rgba(255,255,255,0.16)", borderRadius:50, padding:"11px 18px", color:"#fff", fontSize:"0.9rem", fontFamily:"inherit", outline:"none" }}
              />
              <textarea
                value={contactMsg}
                onChange={(e) => setContactMsg(e.target.value)}
                placeholder="Your message..."
                rows={4}
                style={{ background:"rgba(255,255,255,0.08)", border:"1.5px solid rgba(255,255,255,0.16)", borderRadius:16, padding:"11px 18px", color:"#fff", fontSize:"0.9rem", fontFamily:"inherit", outline:"none", resize:"vertical" }}
              />
              <Btn onClick={handleContact} disabled={contactSending || !contactEmail} variant="primary" style={{ padding:"12px 28px", fontSize:"0.95rem" }}>
                {contactSending ? "Sending…" : "Send Message"}
              </Btn>
            </div>
          )}
        </div>
      </section>

      <Footer setPage={setPage} />

      <style>{`
        @keyframes splineMaterialize {
          0%   { opacity: 0; }
          25%  { opacity: 0.04; }
          55%  { opacity: 0.25; }
          80%  { opacity: 0.7; }
          100% { opacity: 1; }
        }
        .spline-ready {
          animation: splineMaterialize 5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
      `}</style>
    </div>
  );
}
